import React, { useMemo } from 'react';
import { Card } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatVnCalendarDate, formatVnChartDay } from '../../utils/vnDateTime';
import type { TrendDaily } from '../../types/dashboardFounder';
import { fillMissingDatesInRange } from '../../utils/dashboardFounderUtils';
import { ChartSkeleton } from '../dashboard/ChartSkeleton';
import { DashboardEmptyState } from '../dashboard/DashboardEmptyState';

interface CancelRateTrendChartProps {
  data: TrendDaily[];
  startDate: string;
  endDate: string;
  loading?: boolean;
}

const defaultTrend = (date: string) => ({
  date,
  orders: 0,
  gmv: 0,
  platformFee: 0,
  voucherCost: 0,
  net: 0,
  cancelRate: 0,
  newUsers: 0,
  returningUsers: 0,
  avgCreateToConfirmMin: 0,
  avgConfirmToDeliveredMin: 0,
  avgTotalMin: 0,
});

export const CancelRateTrendChart: React.FC<CancelRateTrendChartProps> = ({
  data,
  startDate,
  endDate,
  loading = false,
}) => {
  const chartData = useMemo(() => {
    const filled = fillMissingDatesInRange(
      data,
      startDate,
      endDate,
      defaultTrend
    );
    return filled.map(d => ({
      ...d,
      label: formatVnChartDay(d.date),
      cancelRate: Number(d.cancelRate) || 0,
    }));
  }, [data, startDate, endDate]);

  const yDomain = useMemo(() => {
    const maxVal = Math.max(...chartData.map(d => d.cancelRate), 0);
    return [0, Math.max(maxVal + 1, 5)] as [number, number];
  }, [chartData]);

  if (loading) {
    return (
      <Card className="founder-chart-card" style={{ borderRadius: 12 }}>
        <h3 className="founder-chart-title">Cancel Rate Trend</h3>
        <ChartSkeleton height={240} />
      </Card>
    );
  }

  if (!chartData.length) {
    return (
      <Card className="founder-chart-card" style={{ borderRadius: 12 }}>
        <h3 className="founder-chart-title">Cancel Rate Trend</h3>
        <DashboardEmptyState
          message="Chưa có dữ liệu"
          suggestion="Chọn khoảng ngày khác."
        />
      </Card>
    );
  }

  return (
    <Card className="founder-chart-card" style={{ borderRadius: 12 }}>
      <h3 className="founder-chart-title">Cancel Rate Trend</h3>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart
          data={chartData}
          margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e5e7eb"
            vertical={false}
          />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tickFormatter={v => `${v}%`} domain={yDomain} width={40} />
          <Tooltip
            formatter={(value: number) => [`${value.toFixed(1)}%`, 'Tỷ lệ hủy']}
            labelFormatter={label => {
              const item = chartData.find(d => d.label === label);
              return item
                ? formatVnCalendarDate(item.date, 'DD/MM/YYYY')
                : label;
            }}
          />
          <Line
            type="monotone"
            dataKey="cancelRate"
            stroke="#ef4444"
            strokeWidth={2}
            dot={{ r: 2 }}
            name="Tỷ lệ hủy"
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};
